import React, { useState } from 'react';

/**
 * ProductAvatar Component
 * Displays product image or falls back to initials (like Gmail avatars)
 * 
 * @param {string} name - Product name
 * @param {string} image - Product image URL
 * @param {string} size - Size variant: 'sm', 'md', 'lg', 'xl'
 * @param {string} className - Additional CSS classes
 */
const ProductAvatar = ({ name = '', image = null, size = 'md', className = '' }) => {
    const [imageError, setImageError] = useState(false);

    // Extract initials from product name (first letter of each word, max 2)
    const getInitials = (name) => {
        if (!name) return '?';

        const words = name.trim().split(/\s+/);
        if (words.length === 1) {
            // Single word: take first 2 characters
            return words[0].substring(0, 2).toUpperCase();
        }
        // Multiple words: take first letter of first 2 words
        return words.slice(0, 2).map(word => word[0]).join('').toUpperCase();
    };

    // Generate consistent color based on name
    const getColorFromName = (name) => {
        if (!name) return 'bg-gray-400';

        const colors = [
            'bg-blue-500',
            'bg-purple-500',
            'bg-pink-500',
            'bg-red-500',
            'bg-orange-500',
            'bg-yellow-500',
            'bg-green-500',
            'bg-teal-500',
            'bg-cyan-500',
            'bg-indigo-500',
        ];

        // Simple hash function to get consistent color for same name
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }

        return colors[Math.abs(hash) % colors.length];
    };

    // Size configurations
    const sizeClasses = {
        sm: 'w-8 h-8 text-xs',
        md: 'w-10 h-10 text-sm',
        lg: 'w-12 h-12 text-base',
        xl: 'w-16 h-16 text-lg'
    };

    const shouldShowInitials = !image || imageError;

    if (shouldShowInitials) {
        return (
            <div
                className={`${sizeClasses[size]} ${getColorFromName(name)} rounded-lg flex items-center justify-center text-white font-bold shadow-sm ${className}`}
                title={name}
            >
                {getInitials(name)}
            </div>
        );
    }

    return (
        <img
            src={image}
            alt={name}
            onError={() => setImageError(true)}
            className={`${sizeClasses[size]} rounded-lg object-cover shadow-sm ${className}`}
            title={name}
        />
    );
};

export default ProductAvatar;
